//
// # GenerateFromSourceDialog
//
// Modal dialog for "Layout: Generate From Source" (generate a layout from a
// schematic). Combines stack selection and the layout's destination into a
// single form.
//
// The destination (Depot or OpenAccess, as in Save As) only selects the new
// layout's ident. Generation is the same either way and writes nothing; the
// first save writes the layout, creating the OA library if needed.
//

import React, { useState, useCallback } from "react";

import { CommandPaletteDialog } from "./DesignExplorer/CommandPaletteDialog";
import { PathField, PATH_FORMAT_ERROR } from "./DesignExplorer/PathField";
import { isValidPathInput, oaCellviewIdent } from "./DesignExplorer/pathInput";
import {
  DestinationChoice,
  OaAddressFields,
  OA_ADDRESS_FIELDS,
  TechCheckNote,
  oaAddressValid,
  useOaTechCheck,
  type OaAddress,
} from "./DesignExplorer/OaDestination";
import { Ident } from "GenAlphaModel";
import type {
  GenerateFromSourceDestination,
  GenerateFromSourceDialogState,
} from "../types";

interface GenerateFromSourceDialogProps {
  readonly stacks: ReadonlyArray<string>;
  readonly defaultPath: string;
  readonly defaultOa: GenerateFromSourceDialogState["defaultOa"];
  readonly onConfirm: (
    stackId: string,
    destination: GenerateFromSourceDestination,
    rectangular: boolean
  ) => void;
  readonly onCancel: () => void;
}

export function GenerateFromSourceDialog({
  stacks,
  defaultPath,
  defaultOa,
  onConfirm,
  onCancel,
}: GenerateFromSourceDialogProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [layoutPath, setLayoutPath] = useState(defaultPath);

  // The OpenAccess address, pre-filled from the source schematic where possible.
  const [toOpenAccess, setToOpenAccess] = useState(false);
  const [oa, setOa] = useState<OaAddress>(defaultOa);

  // How every multi-finger transistor lays out its segments. Off is the
  // server's default, a single row.
  const [rectangular, setRectangular] = useState(false);

  // Validation state (shown on blur or after a submit attempt).
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = useCallback((field: string) => {
    setTouched((t) => ({ ...t, [field]: true }));
  }, []);

  // A warning, not a gate; see `OaDestination`.
  const techCheck = useOaTechCheck(toOpenAccess, oa.lib);

  const pathValid = isValidPathInput(layoutPath);
  const canSubmit = toOpenAccess ? oaAddressValid(oa) : pathValid;
  const oaIdent = oaCellviewIdent(oa.lib, oa.cell, oa.view);

  const handleConfirm = useCallback(() => {
    setTouched({
      path: true,
      ...Object.fromEntries(OA_ADDRESS_FIELDS.map((f) => [f, true])),
    });
    if (!canSubmit) return;
    const stack = stacks[selectedIndex];
    if (!stack) return;
    onConfirm(
      stack,
      toOpenAccess
        ? {
            kind: "openaccess",
            lib: oaIdent.lib,
            cell: oaIdent.cell,
            view: oaIdent.view,
          }
        : { kind: "depot", layoutPath: layoutPath.trim() },
      rectangular
    );
  }, [
    canSubmit,
    stacks,
    selectedIndex,
    toOpenAccess,
    oaIdent,
    layoutPath,
    rectangular,
    onConfirm,
  ]);

  return (
    <CommandPaletteDialog
      title="Generate From Source"
      hint="Enter to create · Escape to cancel"
      submitLabel="Create Layout"
      canSubmit={canSubmit}
      onSubmit={handleConfirm}
      onClose={onCancel}
    >
      {/* Destination: the Depot, or an OpenAccess library. */}
      <DestinationChoice
        label="Create in"
        name="generate-layout-destination"
        toOpenAccess={toOpenAccess}
        onChange={setToOpenAccess}
      />

      {toOpenAccess && (
        <div
          className="add-instance-param-constraint"
          style={{ fontStyle: "normal" }}
        >
          The layout is generated in memory and written when you save it. The
          library is created then if it does not exist.
        </div>
      )}

      {/* Technology check for the named library. */}
      {toOpenAccess && <TechCheckNote check={techCheck} />}

      {/* Depot destination — one full design path. */}
      {!toOpenAccess && (
        <PathField
          label="Layout path"
          placeholder="library/cell/layout"
          value={layoutPath}
          onChange={setLayoutPath}
          onBlur={() => {
            markTouched("path");
          }}
          invalid={touched.path && !pathValid}
          message={
            touched.path && !pathValid
              ? { text: PATH_FORMAT_ERROR, isError: true }
              : {
                  text: "Full design path for the new layout.",
                  isError: false,
                }
          }
          autoFocus
        />
      )}

      {/* OpenAccess destination. */}
      {toOpenAccess && (
        <OaAddressFields
          address={oa}
          onChange={setOa}
          touched={touched}
          markTouched={markTouched}
          cellHint="OpenAccess cell name."
        />
      )}

      {/* Where the layout will be addressed. An OA save lands in a gateway
          workspace keyed by tenant, PDK and stack. */}
      {canSubmit && (
        <div className="add-instance-param-field">
          <label className="add-instance-param-label">Creating</label>
          <div
            className="add-instance-param-input"
            style={{ color: "var(--color-ide-text-dim)" }}
          >
            {toOpenAccess ? Ident.displayName(oaIdent) : layoutPath.trim()}
          </div>
          <span className="add-instance-param-constraint">
            {toOpenAccess
              ? "library / cell / view. Written when you save, not now."
              : "The full design path in the Depot."}
          </span>
        </div>
      )}

      {/* Segment arrangement for every m > 1 transistor in the schematic. The
          same choice the instance dialog offers per device. */}
      <label className="add-instance-shape-field">
        <input
          type="checkbox"
          checked={rectangular}
          onChange={(e) => {
            setRectangular(e.target.checked);
          }}
        />
        Arrange multi-finger devices as a grid (default: a single row)
      </label>

      {/* Stack selection, needed for either destination. */}
      <div className="add-instance-param-field">
        <label className="add-instance-param-label">Stack</label>
        <div className="flex flex-col gap-1">
          {stacks.map((stack, idx) => (
            <label
              key={stack}
              className="flex items-center gap-2.5 px-2 py-1.5 rounded cursor-pointer"
              style={{
                backgroundColor:
                  selectedIndex === idx
                    ? "var(--color-ide-surface-2)"
                    : "transparent",
              }}
              onClick={() => {
                setSelectedIndex(idx);
              }}
            >
              <input
                type="radio"
                name="stack-selection"
                checked={selectedIndex === idx}
                onChange={() => {
                  setSelectedIndex(idx);
                }}
                style={{ accentColor: "var(--color-ide-accent)" }}
              />
              <span className="text-[13px] text-[var(--color-ide-text)]">
                {stack}
              </span>
            </label>
          ))}
        </div>
      </div>
    </CommandPaletteDialog>
  );
}
